import * as THREE from 'three'
import { Canvas, useFrame, createPortal, useThree } from '@react-three/fiber'
import { useFBO, OrbitControls, useGLTF, Float, PivotControls, useDepthBuffer, SpotLight } from '@react-three/drei'
import { useMemo, useRef, useState } from 'react'
import { useAudioAnalyzer } from './AudioAnalyser'
import { clamp } from 'three/src/math/MathUtils.js';

function Model({ url }: { url: string }) {
    const { scene } = useGLTF(url);
    return <primitive object={scene} />;
}

function OffscreenScene() {
    const cube = useRef<THREE.Mesh>(null!)

    useFrame((_, delta) => {
        cube.current.rotation.x += delta
        cube.current.rotation.y += delta * 1.2
    })

    return (
        <>
            <ambientLight intensity={0.8} />
            <directionalLight position={[3, 3, 3]} />
            <mesh ref={cube}>
                <boxGeometry />
                <meshStandardMaterial color="hotpink" />
            </mesh>
        </>
    )
}

function ProjectedSphere({ texture, projectorCamera }: { texture: THREE.Texture; projectorCamera: THREE.PerspectiveCamera }) {
    const materialRef = useRef<THREE.ShaderMaterial>(null!)

    useFrame(() => {
        if (!materialRef.current || !projectorCamera) return

        projectorCamera.updateMatrixWorld()

        materialRef.current.uniforms.projectorProjectionMatrix.value =
            projectorCamera.projectionMatrix
        materialRef.current.uniforms.projectorViewMatrix.value =
            projectorCamera.matrixWorldInverse
    })

    return (
        <mesh position={[0, 0, 0]}>
            <sphereGeometry args={[1, 32, 32]} />
            <shaderMaterial
                ref={materialRef}
                uniforms={{
                    map: { value: texture },
                    projectorProjectionMatrix: { value: new THREE.Matrix4() },
                    projectorViewMatrix: { value: new THREE.Matrix4() }
                }}
                vertexShader={`
          precision highp float;

          uniform mat4 projectorProjectionMatrix;
          uniform mat4 projectorViewMatrix;

          varying vec4 vProjPos;

          void main() {
            vec4 worldPos = modelMatrix * vec4(position, 1.0);
            vProjPos = projectorProjectionMatrix * projectorViewMatrix * worldPos;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `}
                fragmentShader={`
          precision highp float;

          uniform sampler2D map;
          varying vec4 vProjPos;

          void main() {
            vec3 proj = vProjPos.xyz / vProjPos.w;
            vec2 uv = proj.xy * 0.5 + 0.5;

            vec3 base = vec3(0.0, 0.35, 0.8); // BLUE sphere

            if (uv.x >= 0.0 && uv.x <= 1.0 && uv.y >= 0.0 && uv.y <= 1.0) {
              gl_FragColor = vec4(texture2D(map, uv).rgb, 1.0);
            } else {
              gl_FragColor = vec4(base, 1.0);
            }
          }
        `}
            />
        </mesh>
    )
}

function MovingSpot({ vec = new THREE.Vector3(), speed = 7, offset = 0, ...props }) {
    const light = useRef<THREE.SpotLight>(null!)
    const viewport = useThree((state) => state.viewport)
    useFrame((state) => {
        // light.current.target.position.lerp(vec.set((state.mouse.x * viewport.width) / 2, (state.mouse.y * viewport.height) / 2, 0), 0.1)
        if (light.current != null) {
            light.current.target.position.lerp(vec.set(Math.sin(state.clock.elapsedTime * speed) * 2 + offset, Math.cos(state.clock.elapsedTime * 1), 0), 0.2)
            light.current.target.updateMatrixWorld()
        }

    })
    return <SpotLight ref={light} penumbra={1} distance={8} angle={0.2} attenuation={8} anglePower={7} intensity={20} {...props} />
}
const SquidgameScene = ({ vocalData, drumData }: { vocalData: number, drumData: number }) => {
    const fbo = useFBO(512, 512)
    const offscreenScene = useMemo(() => new THREE.Scene(), [])
    const offscreenCamera = useMemo(
        () => new THREE.PerspectiveCamera(60, 1, 0.1, 100),
        []
    )
    const depthBuffer = useDepthBuffer({ frames: 1 })
    offscreenCamera.position.z = 3
    offscreenCamera.lookAt(0, 0, 0)
    offscreenCamera.updateProjectionMatrix()
    offscreenCamera.updateMatrixWorld()


    const base = useRef<THREE.ColorRepresentation>(new THREE.Color('black'))
    const colorA = useRef(new THREE.Color('teal'))
    const colorB = useRef(new THREE.Color('#ff4fa3')) // pink
    const temp = useRef(new THREE.Color())
    const oscillated = useRef(new THREE.Color())
    const final = useRef(new THREE.Color())

    useFrame(({ gl, clock }) => {
        gl.setRenderTarget(fbo)
        gl.clear()
        gl.render(offscreenScene, offscreenCamera)
        gl.setRenderTarget(null)
        const t = (Math.sin(clock.elapsedTime) + 1) / 2
        oscillated.current.lerpColors(colorA.current, colorB.current, t)

        // 2️⃣ black ↔ oscillated color
        // const mix = THREE.MathUtils.clamp(otherData, 0, 0.1)
        // final.current.lerpColors(base.current, oscillated.current, mix)

        gl.setClearColor(base.current)

    })

    return (
        <>
            {/* Render cube into the FBO */}
            {createPortal(<OffscreenScene />, offscreenScene)}


            {/* Show framebuffer normally */}
            {/* <mesh position={[5, 0, 0]}>
                <planeGeometry args={[1.5, 1.5]} />
                <meshBasicMaterial map={fbo.texture} />
            </mesh> */}
            {/* <mesh position={[3, 3, 3]}>
                <sphereGeometry args={[1, 32, 32]} />
                <meshStandardMaterial color="orange" />
            </mesh> */}

            {/* Project framebuffer onto a sphere */}
            {/* <ambientLight intensity={0.3} color="green" />
            <directionalLight position={[3,3, 3]} color="orange" intensity={1.0} /> */}
            {/* <ProjectedSphere texture={fbo.texture} projectorCamera={offscreenCamera} /> */}
            {/* <ambientLight intensity={0.3} color="green" /> */}
            <directionalLight position={[3, 3, 3]} color="teal" intensity={30} />
            <pointLight color="white" intensity={THREE.MathUtils.lerp(10, 240, vocalData)}
                distance={10} position={[0, 0, 2]} />
            {/* <pointLight color="hotpink" intensity={THREE.MathUtils.lerp(10, 40, 1)}
                distance={10} position={[0, -2, 2]} /> */}
            {/* <mesh position={[0, -1.5, 2]}>
                <sphereGeometry args={[1, 2, 2]} />
                <meshStandardMaterial color="hotpink" />
            </mesh> */}

            <MovingSpot depthBuffer={depthBuffer} color="white" angle={-0.2} speed={8} offset={0.5} position={[1, 3, 3]}
                intensity={THREE.MathUtils.lerp(0, 20, drumData)} attenuation={THREE.MathUtils.lerp(0, 20, drumData)} />
            <MovingSpot depthBuffer={depthBuffer} color="hot-pink" angle={0.2} position={[-2, 3, 3]}
                intensity={THREE.MathUtils.lerp(0, 20, drumData)} attenuation={THREE.MathUtils.lerp(0, 20, drumData)} />
            <MovingSpot depthBuffer={depthBuffer} color="#b00c3f" position={[0, 3, 3]}
                intensity={THREE.MathUtils.lerp(0, 20, drumData)} attenuation={THREE.MathUtils.lerp(0, 20, drumData)} />
            <Float scale={0.75} position={[0, 0, 0]} rotation={[0, 0, 0]} rotationIntensity={0} floatIntensity={2} speed={3}>

                {/* <PivotControls anchor={[0, 0.7, 0.09]} depthTest={true} scale={0.5} lineWidth={2}> */}
                {/* <ambientLight intensity={0.3} color="green" /> */}
                <mesh position={[0, 0, 0]}>
                    <ambientLight intensity={5} color="teal" />
                    <pointLight color="hotpink" intensity={THREE.MathUtils.lerp(10, 40, 1)}
                        distance={10} position={[0, -2, 2]} />
                    <Model url="/scene.gltf" />
                </mesh>


                {/* </PivotControls> */}
            </Float>
            {/* <Model url="/squid_game_boss_mask/scene.gltf" /> */}

            <OrbitControls />
            {/* <ambientLight intensity={0.8} /> */}
        </>
    )
}

function audioHelper(value: number) {
    const min = 0.05
    const max = 0.6
    let t = (value - min) / (max - min)
    value = clamp(t, min, max)
    t = THREE.MathUtils.inverseLerp(min, max, value)
    return t
}

const SquidgameVisualizer = () => {
    const vocalAudioRef = useRef<HTMLAudioElement>(null);
    const vocaldata = useAudioAnalyzer(vocalAudioRef, true);
    const drumAudioRef = useRef<HTMLAudioElement>(null);
    const drumdata = useAudioAnalyzer(drumAudioRef, true);
    const songAudioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);


    const drumvalue = audioHelper(drumdata.treble);
    const vocalvalue = audioHelper(vocaldata.treble);
    return (
        <div className='w-full h-full relative'>
            <audio ref={vocalAudioRef} src="/music/Fantastic Baby_vocals.wav" />
            <audio ref={drumAudioRef} src="/music/Fantastic Baby_drums.wav" />
            <audio ref={songAudioRef} src="/music/Fantastic Baby.mp3" />
            <div className="absolute top-2 left-2 z-10">
                <button className="btn btn-primary btn-sm" onClick={() => {
                    if (vocalAudioRef.current && isPlaying === false) {
                        vocalAudioRef.current.play();
                        drumAudioRef.current?.play();
                        songAudioRef.current?.play();
                        setIsPlaying(true);
                    } else {
                        vocalAudioRef.current?.pause();
                        drumAudioRef.current?.pause();
                        songAudioRef.current?.pause();
                        setIsPlaying(false);
                    }
                }}>{isPlaying ? "Pause" : "Play"} Audio  </button>
            </div>

            <Canvas camera={{ position: [0, 0, 4], fov: 50, }} >
                <SquidgameScene vocalData={vocalvalue} drumData={drumvalue} />
            </Canvas >
        </div>
    )
}

export default SquidgameVisualizer;